/**
 * Shape-check request/response contract. ARCH-009 §3, PRD-003 PR-11, PR-12,
 * AI-11.
 *
 * ARCH-009 §3 names `packages/api-contracts/src/shape-check.ts` as the home of
 * this pair. This monorepo has no `api-contracts` package; the single source
 * of truth for shared Zod schemas is `@studio/api-schemas` (its index docstring
 * states "Zod schemas shared across apps/portal, server handlers, and
 * @studio/studio-mcp"), and the consume-only UI client
 * (`apps/portal/src/lib/queries/task-intake.ts`) already imports `TaskType` /
 * `CoachingDepth` from here. Placing the contract here satisfies the ARCH-004
 * single-source-of-truth intent the ADR is actually reaching for. The path
 * divergence is recorded in IMPL-NOTES; no behavior changes.
 *
 * The response schema is the exact shape the UI's `ShapeCheckResponse`
 * interface and `SOFT_WARNING_FALLBACK` literal already model, so the server
 * and client stay in lockstep.
 */

import { z } from 'zod';
import { coachingDepthSchema, taskTypeSchema, tierSchema } from './enums.js';

/**
 * ARCH-009 §3 request schema. `brief` length is bounded both to keep the
 * per-call token budget under the §6 ceiling and to limit the prompt-injection
 * surface (§Risks: subscriber input is untrusted).
 */
export const shapeCheckRequestSchema = z.object({
  brief: z.string().min(1).max(4000),
  task_type: taskTypeSchema,
  coaching_depth: coachingDepthSchema,
  tier: tierSchema,
});
export type ShapeCheckRequestPayload = z.infer<typeof shapeCheckRequestSchema>;

/**
 * Risk kinds the model may flag. `out_of_tier_cycle` is the signal that the
 * brief is too large for the tier cycle window (72h Studio, 48h Pro) and
 * pairs with a `split` verdict per PR-12.
 */
export const shapeCheckRiskSchema = z.object({
  kind: z.enum(['scope', 'ambiguity', 'missing_context', 'out_of_tier_cycle']),
  message: z.string(),
});
export type ShapeCheckRisk = z.infer<typeof shapeCheckRiskSchema>;

export const shapeCheckProposedChildSchema = z.object({
  title: z.string(),
  brief: z.string(),
  task_type: taskTypeSchema,
});
export type ShapeCheckProposedChild = z.infer<
  typeof shapeCheckProposedChildSchema
>;

/**
 * ARCH-009 §3 response schema. `verdict` semantics per §4:
 *   ok           - no tightening; subscriber confirms, task -> backlog.
 *   tighten      - tightened phrasing proposed plus optional risks.
 *   split        - oversized for tier cycle; 2-4 children proposed.
 *   soft_warning - a risk surfaces but no tightening; also the §5 fail-open
 *                  verdict when the provider is unavailable.
 */
export const shapeCheckResponseSchema = z.object({
  verdict: z.enum(['ok', 'tighten', 'split', 'soft_warning']),
  tightened_brief: z.string().nullable(),
  flagged_risks: z.array(shapeCheckRiskSchema),
  proposed_split: z.array(shapeCheckProposedChildSchema).nullable(),
  model: z.string(),
  prompt_version: z.string(),
  latency_ms: z.number().int(),
});
export type ShapeCheckResponsePayload = z.infer<
  typeof shapeCheckResponseSchema
>;
